import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {MutationObserver | null} */
  #termsObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    // --- Terms checkbox / GST field handling ---
    // Delegated on `this` because it survives the cart section morph;
    // the checkbox, checkout button, and GST inputs inside get replaced
    // by every morph, so we always re-query them fresh rather than
    // caching references.
    this.addEventListener('change', this.#handleTermsChange);
    this.addEventListener('click', this.#handleTermsClick);
    this.addEventListener('input', this.#handleGstInput);
    this.addEventListener('focusout', this.#handleGstBlur);

    this.#termsObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName === 'data-min-order-disabled' || m.type === 'childList') {
          this.#updateCheckoutButtonState();
          break;
        }
      }
    });
    this.#termsObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-min-order-disabled'],
      childList: true,
      subtree: true,
    });

    this.#updateCheckoutButtonState();

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();

    this.removeEventListener('change', this.#handleTermsChange);
    this.removeEventListener('click', this.#handleTermsClick);
    this.removeEventListener('input', this.#handleGstInput);
    this.removeEventListener('focusout', this.#handleGstBlur);
    this.#termsObserver?.disconnect();
    this.#termsObserver = null;
  }

  /**
   * Re-derives and applies the checkout button's disabled state from the
   * checkbox's current checked state and the min-order flag. Always
   * re-queries the nodes since cart morphs replace them.
   */
  #updateCheckoutButtonState = () => {
    const checkboxEl = this.querySelector('.js-cart-tc-checkbox');
    const checkoutBtn = this.querySelector('.js-cart-checkout-btn');
    if (!checkboxEl || !checkoutBtn) return;

    // Force-unlock in case a morph reapplied a stale `disabled` attribute
    // from the no-JS Liquid default.
    checkboxEl.disabled = false;

    const minOrderDisabled = checkoutBtn.dataset.minOrderDisabled === 'true';
    const shouldEnable = !minOrderDisabled && checkboxEl.checked;
    checkoutBtn.disabled = !shouldEnable;
  };

  /** @param {Event} event */
  #handleTermsChange = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target?.classList?.contains('js-cart-tc-checkbox')) {
      this.#updateCheckoutButtonState();
    }
  };

  /** @param {Event} event */
  #handleTermsClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target?.classList?.contains('js-cart-tc-checkbox')) {
      requestAnimationFrame(this.#updateCheckoutButtonState);
    }
  };

  /** @param {Event} event */
  #handleGstInput = (event) => {
    const target = /** @type {HTMLInputElement} */ (event.target);
    if (!target?.classList?.contains('js-cart-gst-number')) return;

    target.value = target.value.toUpperCase();
    target.classList.remove('error');
    const errEl = this.querySelector('.js-cart-gst-error');
    if (errEl) errEl.style.display = 'none';
  };

  /** @param {Event} event */
  #handleGstBlur = (event) => {
    const target = /** @type {HTMLInputElement} */ (event.target);
    if (!target?.classList?.contains('js-cart-gst-number')) return;

    const val = target.value.trim();
    if (val === '') return; // optional field, blank is fine

    const errEl = this.querySelector('.js-cart-gst-error');
    if (!GST_REGEX.test(val)) {
      target.classList.add('error');
      if (errEl) errEl.style.display = '';
    }
  };

  disconnectedCallback_unused() {} // placeholder removed below

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}